import React from "react";
import PropTypes from "prop-types";
import { Card, Divider, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCurrency } from "../../utils/currency";

function BienCard({ bien, onEdit, onDelete }) {
  const isInmueble = bien.tipo_bien === "INMUEBLE";

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: "300px",
        maxHeight: "400px",
        width: "350px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SuiBox p={1} display="flex" flexDirection="column" height="100%">
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={9}>
            {/* Tipo de Bien */}
            <SuiTypography variant="h6" fontWeight="medium" textTransform="uppercase" gutterBottom>
              {isInmueble ? bien.tipo_inmueble : bien.tipo_mueble}
            </SuiTypography>
          </Grid>
          <Grid size={3}>
            {/* Botones de acción */}
            <IconButton
              onClick={() => onEdit(bien)}
              size="small"
              sx={{
                mr: 1,
                color: "info.main",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              <EditIcon fontSize="small" color="secondary" />
            </IconButton>
            <IconButton
              onClick={() => onDelete(bien)}
              size="small"
              sx={{
                color: "error.main",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
        <Divider sx={{ my: 0.25 }} />
        {/* Detalles específicos según tipo de bien */}
        <SuiBox py={2} flexGrow={1}>
          {isInmueble ? (
            // Campos para bienes inmuebles
            <>
              <SuiBox mb={0.1}>
                <SuiTypography variant="button" fontWeight="medium">
                  DIRECCIÓN:
                </SuiTypography>
                <SuiTypography
                  variant="button"
                  fontWeight="regular"
                  ml={1}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {bien.direccion}
                </SuiTypography>
              </SuiBox>

              <SuiBox mb={0.1}>
                <SuiTypography variant="button" fontWeight="medium">
                  MATRÍCULA INMOBILIARIA:
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" ml={1}>
                  {bien.matricula_inmobiliaria}
                </SuiTypography>
              </SuiBox>
            </>
          ) : (
            // Campos para bienes muebles
            <>
              <SuiBox mb={0.1}>
                <SuiTypography variant="button" fontWeight="medium">
                  PLACA:
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" ml={1}>
                  {bien.placa}
                </SuiTypography>
              </SuiBox>

              <SuiBox mb={0.1}>
                <SuiTypography variant="button" fontWeight="medium">
                  MARCA / MODELO:
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" ml={1}>
                  {bien.marca} / {bien.modelo}
                </SuiTypography>
              </SuiBox>

              <SuiBox mb={0.1}>
                <SuiTypography variant="button" fontWeight="medium">
                  COLOR:
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" ml={1}>
                  {bien.color}
                </SuiTypography>
              </SuiBox>
            </>
          )}

          {/* Campos comunes */}
          <SuiBox mb={0.1}>
            <SuiTypography variant="button" fontWeight="medium">
              AVALÚO CATASTRAL:
            </SuiTypography>
            <SuiTypography variant="button" fontWeight="regular" ml={1}>
              {formatCurrency(bien.avaluo_catastral).formatted}
            </SuiTypography>
          </SuiBox>

          {bien.afectaciones && (
            <SuiBox>
              <SuiTypography variant="button" fontWeight="medium">
                AFECTACIONES:
              </SuiTypography>
              <SuiTypography
                variant="button"
                fontWeight="regular"
                component="div"
                ml={1}
                mt={0.5}
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {bien.afectaciones}
              </SuiTypography>
            </SuiBox>
          )}
        </SuiBox>
      </SuiBox>
    </Card>
  );
}

BienCard.propTypes = {
  bien: PropTypes.shape({
    id: PropTypes.number.isRequired,
    tipo_bien: PropTypes.oneOf(["INMUEBLE", "MUEBLE"]).isRequired,
    tipo_inmueble: PropTypes.string,
    tipo_mueble: PropTypes.string,
    direccion: PropTypes.string,
    matricula_inmobiliaria: PropTypes.string,
    placa: PropTypes.string,
    marca: PropTypes.string,
    modelo: PropTypes.string,
    color: PropTypes.string,
    avaluo_catastral: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    afectaciones: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default BienCard;
